import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UserPlus, Camera, Phone, AtSign, CheckCircle2, ArrowLeft,
  ContactRound, MapPin, UserRound, ShieldCheck,
} from "@/lib/icons";
import { Avatar, AVATAR_OPTIONS } from "@/components/PhotoUpload";
import { useSettings } from "@/lib/settings";
import { useSeo } from "@/lib/seo";

/**
 * Public "Add a friend" referral form.
 * Captures editable contact details and an optional avatar, then creates a pending member
 * in the admin dashboard. Credentials are generated only after admin approval.
 */
export default function AddFriend() {
  const { settings } = useSettings();
  const emptyForm = { full_name: "", studio_name: "", mobile: "", email: "", pincode: "", address: "", avatar_key: "" };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useSeo({ title: "Refer a Friend | PRONET", noindex: true });
  const configuredAvatars = AVATAR_OPTIONS.filter((option) =>
    (settings?.avatar_presets || []).some((preset) => preset.key === option.key && preset.image_url)
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const mobile = (form.mobile || "").replace(/\D/g, "").slice(-10);
    if (mobile.length !== 10 || !"6789".includes(mobile[0])) {
      setErr("Enter a valid 10-digit Indian mobile number");
      return;
    }
    if (!form.full_name.trim()) {
      setErr("Friend's name is required");
      return;
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      setErr("Enter a valid 6-digit pincode");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/add-friend", {
        full_name: form.full_name.trim(),
        studio_name: form.studio_name.trim(),
        mobile,
        email: form.email.trim() || null,
        pincode: form.pincode.trim(),
        address: form.address.trim() || null,
        avatar_key: form.avatar_key || null,
      });
      setDone(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const importContact = async () => {
    setErr("");
    if (!navigator.contacts?.select) {
      toast.info("Contact import works in Chrome on supported Android phones. Try Chrome, or enter the details manually.");
      return;
    }
    setContactBusy(true);
    try {
      const available = navigator.contacts.getProperties
        ? await navigator.contacts.getProperties()
        : ["name", "tel", "email", "address"];
      const properties = ["name", "tel", "email", "address"].filter((key) => available.includes(key));
      const contacts = await navigator.contacts.select(properties, { multiple: false });
      const contact = contacts?.[0];
      if (!contact) return;
      const firstValue = (value) => Array.isArray(value) ? value[0] : value;
      const asText = (value) => {
        const item = firstValue(value);
        return typeof item === "string" ? item : (item?.value || "");
      };
      const addressItem = firstValue(contact.address);
      const addressLines = Array.isArray(addressItem?.addressLine)
        ? addressItem.addressLine
        : (addressItem?.addressLine ? [addressItem.addressLine] : []);
      const address = typeof addressItem === "string"
        ? addressItem
        : [
            ...addressLines,
            addressItem?.city, addressItem?.region,
            addressItem?.postalCode, addressItem?.country,
          ].filter(Boolean).join(", ");
      const mobile = asText(contact.tel).replace(/\D/g, "").slice(-10);
      setForm((current) => ({
        ...current,
        full_name: asText(contact.name) || current.full_name,
        mobile: mobile || current.mobile,
        email: asText(contact.email) || current.email,
        address: address || current.address,
      }));
      toast.success("Contact added. You can edit every field before submitting.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        toast.error("Could not open contacts. You can still enter the details manually.");
      }
    } finally {
      setContactBusy(false);
    }
  };

  if (done) {
    return (
      <div className="relative container-tight py-12 md:py-20 max-w-md">
        <div className="blobs-layer">
          <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
          <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
        </div>
        <div className="relative glass rounded-3xl p-6 sm:p-10 text-center fade-up-slow">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 mb-4">
            <CheckCircle2 size={26} />
          </div>
          <div className="overline text-fuchsia-300">Request submitted</div>
          <h1 className="mt-2 font-display text-3xl text-white">Thanks for referring your friend</h1>
          <p className="mt-3 text-sm text-white/60">
            An admin will review this request shortly. Once approved, we'll email the login details to
            <br /><span className="text-fuchsia-300">{form.email || "the mobile number provided"}</span>.
          </p>
          <div className="mt-6 text-xs text-white/50">
            Reference ID: <span className="font-mono text-white/80">{done.member_id}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            <Link to="/" data-testid="add-friend-done-home"
              className="btn-ghost rounded-full px-5 py-2 text-sm font-semibold text-white">Back home</Link>
            <button onClick={() => { setDone(null); setForm(emptyForm); }}
              data-testid="add-friend-done-another"
              className="btn-glow rounded-full px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2">
              <UserPlus size={13} /> Add another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative container-tight py-12 md:py-20 max-w-2xl">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
        <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
      </div>
      <form onSubmit={submit} className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-fuchsia-300">
          <ArrowLeft size={12} /> Back home
        </Link>
        <div className="mt-3 overline text-fuchsia-300">Add friend</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl leading-none text-white">
          Add your friends to the <span className="italic text-gradient">ProNet</span>
        </h1>
        <p className="mt-2 text-sm text-white/60">
          Refer a fellow photographer. We will help them complete their PRONET onboarding.
        </p>

        <div className="mt-6 rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/[0.06] p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <ContactRound size={16} className="text-fuchsia-300" /> Add from contacts
              </div>
              <p className="mt-1 text-xs text-white/50">Only the one contact you choose is shared. All imported fields stay editable.</p>
            </div>
            <button
              type="button"
              onClick={importContact}
              disabled={contactBusy}
              data-testid="af-import-contact"
              className="btn-ghost shrink-0 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
            >
              {contactBusy ? "Opening…" : "Choose contact"}
            </button>
          </div>
          <div className="mt-2 text-[10px] text-white/35">Available in Chrome on supported Android phones. Other browsers can use manual entry.</div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field
            label="Friend's name"
            icon={UserRound}
            required
            testid="af-full-name"
            placeholder="e.g. Ananya Sharma"
            value={form.full_name}
            onChange={(v) => setForm({ ...form, full_name: v })}
          />
          <Field
            label="Studio name (optional)"
            icon={Camera}
            testid="af-studio-name"
            placeholder="e.g. Kangru Photo Studio"
            value={form.studio_name}
            onChange={(v) => setForm({ ...form, studio_name: v })}
          />
          <Field
            label="Mobile number"
            icon={Phone}
            required
            testid="af-mobile"
            placeholder="10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
            value={form.mobile}
            onChange={(v) => setForm({ ...form, mobile: v.replace(/\D/g, "").slice(0, 10) })}
          />
          <Field
            label="Email (optional)"
            icon={AtSign}
            testid="af-email"
            type="email"
            placeholder="friend@example.com"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
          />
          <div>
            <Field
              label="Pincode"
              icon={MapPin}
              required
              testid="af-pincode"
              placeholder="6-digit pincode"
              inputMode="numeric"
              maxLength={6}
              value={form.pincode}
              onChange={(v) => setForm({ ...form, pincode: v.replace(/\D/g, "").slice(0, 6) })}
            />
            <div className="mt-1.5 text-[11px] text-white/40">City, district and state are detected automatically from this.</div>
          </div>
          <Field
            label="Address (optional)"
            icon={MapPin}
            testid="af-address"
            placeholder="Street and locality"
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
          />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="overline text-white/60 text-[10px]">Profile avatar (optional)</div>
              <p className="mt-1 text-xs text-white/40">Choose a profile image, or leave blank to use their initials.</p>
            </div>
            {form.avatar_key && (
              <button type="button" onClick={() => setForm({ ...form, avatar_key: "" })}
                className="text-xs text-white/50 hover:text-fuchsia-300" data-testid="af-avatar-clear">
                Use initials
              </button>
            )}
          </div>
          {configuredAvatars.length > 0 ? (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" role="radiogroup" aria-label="Profile avatar">
            {configuredAvatars.map((option) => {
              const selected = form.avatar_key === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={option.label}
                  onClick={() => setForm({ ...form, avatar_key: option.key })}
                  data-testid={`af-avatar-${option.key}`}
                  className={`relative rounded-2xl p-1 transition ${
                    selected
                      ? "ring-2 ring-fuchsia-400 ring-offset-2 ring-offset-[#0f0818]"
                      : "opacity-75 hover:opacity-100 hover:-translate-y-0.5"
                  }`}
                >
                  <Avatar name={form.full_name || "PRONET member"} avatarKey={option.key}
                    size="100%" shape="rounded" bordered={false} className="aspect-square" />
                  {selected && (
                    <span className="absolute right-0 top-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#9D00FF] text-white shadow-lg">
                      <CheckCircle2 size={14} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          ) : (
            <div className="mt-3 rounded-xl border border-dashed border-white/15 bg-white/[0.03] px-4 py-5 text-center text-xs text-white/45">
              No avatar images are available yet. Their initials will be used automatically.
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 text-[10px] text-white/40">
            <ShieldCheck size={12} className="text-fuchsia-300" />
            The avatar appears after admin approval and can later be replaced with a real photo.
          </div>
        </div>

        {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="af-error">{err}</div>}

        <button type="submit" disabled={busy} data-testid="af-submit"
          className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <UserPlus size={15} /> {busy ? "Submitting…" : "Submit for review"}
        </button>
        <p className="mt-4 text-[11px] text-white/40 text-center">
          Login credentials are generated only after an admin approves this request.
        </p>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, testid, value, onChange, required, ...rest }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <div className="relative mt-2">
        {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />}
        <Input required={required} data-testid={testid}
          className={`${Icon ? "pl-9" : ""} h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40`}
          value={value} onChange={(e) => onChange(e.target.value)}
          {...rest} />
      </div>
    </div>
  );
}
